import {useCallback, useEffect, useState} from 'react'
import {ApiError, getBook} from '../lib/api'
import type {BookSummary} from '../types/models'

export function useBookDetail(bookId: string) {
    const [book, setBook] = useState<BookSummary | null>(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)
    const [trackedId, setTrackedId] = useState(bookId)

    // Route reuses this component for book-to-book: reset during render, not in an effect.
    if (bookId !== trackedId) {
        setTrackedId(bookId)
        setBook(null)
        setError(null)
        setLoading(true)
    }

    const load = useCallback(async () => {
        setLoading(true)
        setError(null)
        try {
            setBook(await getBook(bookId))
        } catch (e) {
            setError(e instanceof ApiError ? e.message : 'Failed to load book')
        } finally {
            setLoading(false)
        }
    }, [bookId])

    // setState only in async continuations (react-hooks/set-state-in-effect).
    useEffect(() => {
        if (!bookId) return
        let cancelled = false
        getBook(bookId)
            .then((b) => { if (!cancelled) setBook(b) })
            .catch((e) => { if (!cancelled) setError(e instanceof ApiError ? e.message : 'Failed to load book') })
            .finally(() => { if (!cancelled) setLoading(false) })
        return () => { cancelled = true }
    }, [bookId])

    return {book, loading, error, refresh: load}
}
